import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '../lib/utils'
import { useAuthStore } from '../store/authStore'
import { NAV_SECTIONS, type NavItem } from './navigation'
import { useRecursos } from '../features/plano/useRecurso'
import logoLeao from '../assets/logo-leao.png'

interface SidebarProps {
  collapsed: boolean
  mobileOpen: boolean
  onCloseMobile: () => void
}

function isGroupActive(item: NavItem, pathname: string) {
  return !!item.children?.some((c) => pathname.startsWith(c.to))
}

export function Sidebar({ collapsed, mobileOpen, onCloseMobile }: SidebarProps) {
  const { pathname } = useLocation()
  const ehAdmin = useAuthStore((s) => s.userRole) === 'ADMIN'
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  const { recursos } = useRecursos()
  // Só trava com o mapa carregado e o recurso desabilitado (ou ausente).
  const bloqueado = (item: NavItem) => !!item.recurso && !!recursos && !recursos[item.recurso]?.habilitado
  const secoes = NAV_SECTIONS.filter((s) => !s.somenteAdmin || ehAdmin)

  const isOpen = (item: NavItem) => openGroups[item.label] ?? isGroupActive(item, pathname)
  const toggle = (item: NavItem) => setOpenGroups((g) => ({ ...g, [item.label]: !isOpen(item) }))

  // Mobile drawer always shows the full menu.
  const compact = collapsed && !mobileOpen

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden animate-fade-in" onClick={onCloseMobile} aria-hidden />}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-sidebar border-r border-border/60 transition-[width,transform] duration-200 lg:static lg:z-auto lg:translate-x-0',
          compact ? 'w-[70px]' : 'w-[250px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Menu principal"
      >
        <div className={cn('flex h-[70px] flex-shrink-0 items-center gap-3', compact ? 'justify-center' : 'px-6')}>
          <img src={logoLeao} alt="" className="h-9 w-auto" />
          {!compact && <span className="text-base font-bold tracking-[0.2em] text-white">WALLEON</span>}
        </div>

        <nav className={cn('flex-1 pb-6', compact ? 'overflow-visible' : 'overflow-y-auto')}>
          {secoes.map((section) => (
            <div key={section.title}>
              {compact ? (
                <div className="flex h-10 items-center justify-center text-muted/60" aria-hidden>
                  <i className="ri-more-fill" />
                </div>
              ) : (
                <p className="px-6 pb-2 pt-5 text-2xs font-semibold uppercase tracking-[0.05em] text-muted/70">{section.title}</p>
              )}

              <ul>
                {section.items.map((item) => {
                  const groupActive = isGroupActive(item, pathname)
                  const itemClass = (active: boolean) => cn(
                    'group/link flex w-full items-center text-15 transition-colors',
                    compact ? 'h-12 justify-center' : 'gap-2.5 px-6 py-2.5',
                    active ? 'text-white' : 'text-muted hover:text-white',
                  )
                  const icon = (active: boolean) => (
                    <i className={cn(item.icon, 'text-lg leading-none', active ? 'text-primary' : 'group-hover/link:text-white')} aria-hidden />
                  )

                  if (!item.children) {
                    const travado = bloqueado(item)
                    return (
                      <li key={item.label} className="group relative">
                        <NavLink
                          to={travado ? '/plano' : item.to!}
                          onClick={onCloseMobile}
                          className={({ isActive }) => itemClass(isActive && !travado)}
                          title={travado ? `${item.label} (não incluído no seu plano)` : compact ? item.label : undefined}
                        >
                          {({ isActive }) => (
                            <>
                              {icon(isActive && !travado)}
                              {!compact && <span className="truncate">{item.label}</span>}
                              {!compact && travado && <i className="ri-lock-line ml-auto text-base text-muted" aria-hidden />}
                            </>
                          )}
                        </NavLink>
                        {compact && (
                          <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-1 hidden -translate-y-1/2 whitespace-nowrap rounded bg-surface2 px-3 py-1.5 text-13 text-white shadow-dropdown group-hover:block">
                            {item.label}
                          </span>
                        )}
                      </li>
                    )
                  }

                  const open = isOpen(item)
                  return (
                    <li key={item.label} className="group relative">
                      <button type="button" onClick={() => !compact && toggle(item)} className={itemClass(groupActive)} aria-expanded={compact ? undefined : open}>
                        {icon(groupActive)}
                        {!compact && (
                          <>
                            <span className="flex-1 truncate text-left">{item.label}</span>
                            <i className={cn('ri-arrow-right-s-line text-lg transition-transform', open && 'rotate-90')} aria-hidden />
                          </>
                        )}
                      </button>

                      {compact ? (
                        <div className="absolute left-full top-0 z-50 hidden min-w-[190px] rounded-r bg-sidebar py-2 shadow-dropdown group-hover:block">
                          <p className="px-4 pb-1 text-2xs font-semibold uppercase text-muted/70">{item.label}</p>
                          {item.children.map((child) => (
                            <NavLink
                              key={child.to}
                              to={child.to}
                              className={({ isActive }) => cn('block px-4 py-2 text-13 transition-colors', isActive ? 'text-primary' : 'text-muted hover:text-white')}
                            >
                              {child.label}
                            </NavLink>
                          ))}
                        </div>
                      ) : (
                        open && (
                          <ul className="pb-1">
                            {item.children.map((child) => (
                              <li key={child.to}>
                                <NavLink
                                  to={child.to}
                                  onClick={onCloseMobile}
                                  className={({ isActive }) => cn(
                                    'flex items-center py-2 pl-[52px] pr-6 text-13 transition-colors',
                                    "before:mr-2.5 before:h-px before:w-1.5 before:bg-current before:content-['']",
                                    isActive ? 'text-primary' : 'text-muted hover:text-white',
                                  )}
                                >
                                  {child.label}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        )
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  )
}
